import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import styles from './ProductSheet_Media.module.scss';

import Field from '../../widget/inputs/Field';
import FieldMedia from '../../widget/inputs/FieldMedia';
import WidgetFrame from '../../widget/navigation/WidgetFrame';
import CollapsiblePanel from '../../widget/windows/CollapsiblePanel';
import Icon from '../../widget/images/Icon';

import { ProductMedia, initializedMedia } from '../../../dataTypes/productManagment/types';
import { WWW_URLS } from '../../../backendServices/urls';

export interface ErrorMessagesForMedia {
  content: string;
}

export interface ProductSheet_Media_Props {
  media: ProductMedia;
  errorMessages: ErrorMessagesForMedia[];
  indexInErrorMessages: number;
  title: string;
  url?: string;
  width?: string;
  onChange?: (newMedia: ProductMedia) => void;
  onResize?: () => void;
  onDelete?: () => void;
  onThumbnailClick?: () => void;
  open?: boolean;
  publicRef?: string | null;
  withCollapsiblePanel?: boolean;
  externalMode?: boolean;
  showTextInput?: boolean;
  fileInputRef?: React.RefObject<HTMLInputElement>;
  showMediaWithLabel?: boolean;
  showThumbnailStar?: boolean;
  showLegend?: boolean;
  thumbnailStarInComponent?: boolean;
  hasError?: boolean; // ← NOUVELLE LIGNE
}

export const ProductSheet_Media = ({ 
  media,
  errorMessages,
  indexInErrorMessages,
  title,
  url,
  width = '100%',
  onChange,
  onResize,
  onDelete,
  onThumbnailClick,
  open,
  publicRef,
  withCollapsiblePanel = true,
  externalMode = false,
  showTextInput = true,
  fileInputRef,
  showMediaWithLabel = true,
  showThumbnailStar = false,
  showLegend = true,
  thumbnailStarInComponent = true,
  hasError = false // ← NOUVELLE LIGNE
}: ProductSheet_Media_Props) => {
  
  const isEditMode = !!media;
  const initialMedia = isEditMode ? media : initializedMedia();

  const [fields, setFields] = useState({
    name: initialMedia.name || '',
    fileContent: initialMedia.fileContent || null,
  });

  const [messages, setMessages] = useState({
    content: errorMessages?.[indexInErrorMessages]?.content ?? '',
  });

  const [imageFileToUpload, setImageFileToUpload] = useState<File | null>(null);
  const [imageSrc] = useState(
    (publicRef && publicRef !== '' && media.publicFileName && media.publicFileName !== '') 
      ? `${WWW_URLS.MediaServer}${publicRef}/images/${media.publicFileName}.webp` 
      : ''
  );

  useEffect(() => {
    setMessages({
      content: errorMessages?.[indexInErrorMessages]?.content ?? '',
    });
  }, [errorMessages, indexInErrorMessages]);

  const handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string | number | File | Date
  ) => {
    const newValue = value as string;
    
    setFields((prev) => {
      const updatedFields = { ...prev, name: newValue };
      notifyMediaChange({ ...initialMedia, ...updatedFields });
      return updatedFields;
    });
  };

  const handleImageFileToUploadChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string | number | File | Date,
    mediaType?: string,
    duration?: string
  ) => {
    if (event && event.target && event.target.files) {
      const file = event.target.files[0];
      setImageFileToUpload(file);

      const updatedFields = {
        ...fields,
        fileContent: file,
        mediaType: mediaType ?? 'unknown'
      };

      setFields(updatedFields);
      notifyMediaChange({ 
        ...initialMedia, 
        ...updatedFields 
      });

      setMessages((prev) => ({ ...prev, content: '' }));
    } else {
      setMessages((prev) => ({ ...prev, content: 'Il manque le fichier' }));
    }
  };

  const notifyMediaChange = (newMedia: ProductMedia) => {
    if (onChange) {
      onChange(newMedia);
    }
  };

  const handleThumbnailClick = () => {
    if (onThumbnailClick && !media.asThumbnail) {
      onThumbnailClick();
    }
  };

  // Vérifier si un média a effectivement été choisi
  const hasMediaContent = !!(
    imageSrc || 
    imageFileToUpload || 
    fields.fileContent || 
    media.publicFileName
  );

/*
  const renderContent = () => (
    <div className={classNames(styles.contain)} style={{ position: 'relative' }}>
      {showThumbnailStar && thumbnailStarInComponent && media.mediaType === 'Image' && hasMediaContent && (
        <button
          onClick={handleThumbnailClick}
          disabled={media.asThumbnail}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: media.asThumbnail ? 'default' : 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 10,
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!media.asThumbnail) {
              e.currentTarget.style.transform = 'scale(1.1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title={media.asThumbnail ? 'Image de couverture' : 'Définir comme image de couverture'}
        >
          <Icon 
            name='star2' 
            width={20} 
            height={20} 
            color={media.asThumbnail ? '#6632FF' : '#F3EFFF'} 
          />
        </button>
      )}

      {showLegend && (
        <div className={classNames(styles.name)}>
          <WidgetFrame 
            name="Légende"
            labelTextPosition="right"
            classNameGrid={classNames(styles.grid_frame)}
          >
            <Field 
              type="text"
              value={fields.name}
              onChange={(event, value) => handleNameChange(event, value)}
            />
          </WidgetFrame>
        </div>
      )}

      {
       showMediaWithLabel
	   ?
	   (
        <div className={classNames(styles.photo)}>
          <WidgetFrame name="Photo"
                       labelTextPosition="right"
                       error={messages.content}
                       classNameGrid={classNames(styles.grid_frame)} >
            <FieldMedia placeholder=""
                        value={imageSrc}
                        onChange={handleImageFileToUploadChange}
                        externalMode={externalMode}
                        showTextInput={showTextInput}
                        fileInputRef={fileInputRef} />
          </WidgetFrame>
        </div>
	   )
	   :
	   (
        <div className={classNames(styles.photo)}>
            <FieldMedia placeholder=""
                        value={imageSrc}
                        onChange={handleImageFileToUploadChange}
                        externalMode={externalMode}
                        showTextInput={showTextInput}
                        fileInputRef={fileInputRef} />
        </div>
	   )
	  }
    </div>
  );
  
  
  */

 const renderContent = () => (
    <div className={classNames(styles.contain)} style={{ position: 'relative' }}>
      {showThumbnailStar && thumbnailStarInComponent && media.mediaType === 'Image' && hasMediaContent && (
        <button
          onClick={handleThumbnailClick}
          disabled={media.asThumbnail}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: media.asThumbnail ? 'default' : 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 10,
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => {
            if (!media.asThumbnail) {
              e.currentTarget.style.transform = 'scale(1.1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title={media.asThumbnail ? 'Image de couverture' : 'Définir comme image de couverture'}
        >
          <Icon 
            name='star2' 
            width={20} 
            height={20} 
            color={media.asThumbnail ? '#6632FF' : '#F3EFFF'} 
          />
        </button>
      )}

      {showLegend && (
        <div className={classNames(styles.name)}>
          <WidgetFrame 
            name="Légende"
            labelTextPosition="right"
            classNameGrid={classNames(styles.grid_frame)}
          >
            <Field 
              type="text"
              value={fields.name}
              onChange={(event, value) => handleNameChange(event, value)}
            />
          </WidgetFrame>
        </div>
      )}

      {/* ✅ MODIFICATION PRINCIPALE : Afficher le message d'erreur si hasError */}
      {showMediaWithLabel ? (
        <div className={classNames(styles.photo)}>
          <WidgetFrame 
            name="Photo"
            labelTextPosition="right"
            error={messages.content}
            classNameGrid={classNames(styles.grid_frame)}
          >
            <div style={{ position: 'relative' }}>
              <FieldMedia 
                placeholder=""
                value={imageSrc}
                onChange={handleImageFileToUploadChange}
                externalMode={externalMode}
                showTextInput={showTextInput}
                fileInputRef={fileInputRef} 
              />
              {/* ✅ Message d'erreur SI hasError === true 
{hasError && (
  <div style={{
	width:'100%',
    color: '#dc2626',
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'center',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
	margin: 'auto'
  }}>
    Il manque le fichier média
  </div>
)}
      
*/}

{hasError && <div style={{
	width:'100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }}><Icon name='mediaTypeUnknown' color='red' width={150} height={150} /></div>}

      </div>
          </WidgetFrame>
        </div>
      ) : (
        <div className={classNames(styles.photo)}>
          <div style={{ position: 'relative' }}>
            <FieldMedia 
              placeholder=""
              value={imageSrc}
              onChange={handleImageFileToUploadChange}
              externalMode={externalMode}
              showTextInput={showTextInput}
              fileInputRef={fileInputRef} 
            />
            {/* ✅ Message d'erreur SI hasError === true 
{hasError && (
  <div style={{
	width:'100%',
    color: '#dc2626',
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'center',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
	margin: 'auto'
  }}>
    Il manque le fichier média
  </div>
)}

*/}

{hasError && <div style={{
	width:'100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }}><Icon name='mediaTypeUnknown' color='red' width={150} height={150} /></div>}




          </div>
        </div>
      )}
    </div>
  );




  if (withCollapsiblePanel) {
    return (
      <div className={classNames(styles.root)}>
        <CollapsiblePanel 
          title={title} 
          onResize={onResize} 
          onDelete={onDelete} 
          open={open}
        >
          {renderContent()}
        </CollapsiblePanel>
      </div>
    );
  } else {
    return (
      <div className={classNames(styles.root)}>
        {renderContent()}
      </div>
    );
  }
};

export default ProductSheet_Media;